/**
 * productRegistry - Resolves product display metadata for an ARI, ATI, or
 * `thirdPartyId` value returned by the Teamwork Graph.
 *
 * The runtime data is sourced from the bundled
 * `home/product-registry.lite.json` file, which is generated from the larger
 * `home/product-registry.json` via `scripts/build-product-registry-lite.mjs`.
 * The lite file deliberately omits icons, logos, templates, and per-resource
 * metadata so we ship the smallest possible payload.
 *
 * Public surface (intentionally narrow):
 *   - getProductByResourceOwner(resourceOwner)
 *   - getProductForAri(ari)
 *   - getProductForThirdPartyId(thirdPartyId)
 *   - getDisplayProductName(...)
 */

import liteRegistry from '../../product-registry.lite.json';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type Product = {
	resourceOwner: string;
	displayName: string;
	isThirdParty: boolean;
	platform: string;
};

type LiteRegistry = {
	products: Record<string, Product>;
};

// ---------------------------------------------------------------------------
// Internal indices
// ---------------------------------------------------------------------------

const PRODUCTS: Record<string, Product> = (liteRegistry as LiteRegistry).products ?? {};

/**
 * thirdPartyId values returned by AGG are typically the connector resource
 * owner key such as `github.github`, `dropbox.dropbox`, etc.
 *
 * Some connectors emit just the bare product slug (`github`). Build a
 * secondary lookup keyed on the trailing segment so both shapes resolve.
 */
const PRODUCTS_BY_TRAILING_SEGMENT: Record<string, Product> = {};
for (const product of Object.values(PRODUCTS)) {
	const trailing = product.resourceOwner.includes('.')
		? product.resourceOwner.split('.').pop()
		: product.resourceOwner;
	if (trailing && !PRODUCTS_BY_TRAILING_SEGMENT[trailing]) {
		PRODUCTS_BY_TRAILING_SEGMENT[trailing] = product;
	}
}

// ---------------------------------------------------------------------------
// Lookups
// ---------------------------------------------------------------------------

/**
 * Direct lookup keyed on the canonical `resourceOwner` (segment 2 of an ARI
 * or ATI).
 */
export function getProductByResourceOwner(
	resourceOwner: string | null | undefined,
): Product | null {
	if (!resourceOwner) return null;
	return PRODUCTS[resourceOwner] ?? null;
}

/**
 * Resolve the product encoded in an ARI / ATI string.
 *
 * Format reminders:
 *   ari:{platform}:{resourceOwner}:{qualifier}:{resourceType}/{resourceId}
 *   ati:{platform}:{resourceOwner}:{resourceType}
 *
 * Third-party ARIs frequently encode the resourceOwner as `vendor.product`
 * (e.g. `ari:third-party:bitbucket.bitbucket::commit/...`) but the registry
 * may key the connector under either form, so we fall back through:
 *   1. `vendor.product` direct lookup
 *   2. trailing segment lookup (`bitbucket.bitbucket` → `bitbucket`)
 *   3. leading segment lookup (`bitbucket.bitbucket` → `bitbucket` again,
 *      kept as a defensive fallback for `vendor.subproduct` shapes)
 */
export function getProductForAri(ariOrAti: string | null | undefined): Product | null {
	if (!ariOrAti) return null;
	const segments = ariOrAti.split(':');
	if (segments.length < 3) return null;
	const resourceOwner = segments[2];
	if (!resourceOwner) return null;

	const direct = PRODUCTS[resourceOwner];
	if (direct) return direct;

	// `vendor.product` shape — try trailing then leading segment.
	if (resourceOwner.includes('.')) {
		const trailing = resourceOwner.split('.').pop();
		if (trailing && PRODUCTS_BY_TRAILING_SEGMENT[trailing]) {
			return PRODUCTS_BY_TRAILING_SEGMENT[trailing];
		}
		const leading = resourceOwner.split('.')[0];
		if (leading && PRODUCTS_BY_TRAILING_SEGMENT[leading]) {
			return PRODUCTS_BY_TRAILING_SEGMENT[leading];
		}
	} else if (PRODUCTS_BY_TRAILING_SEGMENT[resourceOwner]) {
		return PRODUCTS_BY_TRAILING_SEGMENT[resourceOwner];
	}

	return null;
}

/**
 * Resolve the product implied by a `thirdPartyId` property on an external
 * graph entity.
 *
 * The thirdPartyId values returned by the graph store come in several
 * shapes that we must normalise to a connector key:
 *
 *   - `ari:cloud:bitbucket::pullrequest/abc:123`   (ARI form — segment 2 is the resourceOwner)
 *   - `ari:third-party:github.github:repository/abc`
 *   - `bitbucket::pullrequest/123`                  (`<connector>::<type>/<id>`)
 *   - `github.github::repository/abc`               (third-party — `<vendor>.<product>::<type>/<id>`)
 *   - `dropbox.dropbox`                             (bare resource owner)
 *   - `bitbucket`                                   (bare connector)
 *
 * The strategy:
 *   1. If the value looks like an ARI, route through `getProductForAri`.
 *   2. Otherwise peel the leading `<connector>` token off the front
 *      (everything before the first `::`, `:` or `/`).
 *   3. Try direct resource-owner lookup, trailing-segment lookup
 *      (`vendor.product` → `product`), and leading-segment lookup
 *      (`vendor.product` → `vendor`).
 *
 * Returns null when nothing resolves so callers can fall back gracefully.
 */
export function getProductForThirdPartyId(thirdPartyId: string | null | undefined): Product | null {
	if (!thirdPartyId) return null;

	// ARI form — let the ARI parser handle segment extraction so we pick
	// the actual resourceOwner (e.g. `bitbucket`) rather than the literal
	// "ari" prefix token.
	if (thirdPartyId.startsWith('ari:')) {
		const fromAri = getProductForAri(thirdPartyId);
		if (fromAri) return fromAri;
	}

	// Strip everything from the first `::`, `:` or `/` onward — these
	// separators always sit between the connector token and the entity
	// type / id.
	const connectorToken = thirdPartyId.split(/::|:|\//)[0] ?? thirdPartyId;
	if (!connectorToken) return null;

	// 1. Direct resource-owner lookup (e.g. `bitbucket`, `github.github`).
	const direct = PRODUCTS[connectorToken];
	if (direct) return direct;

	// 2. Trailing segment of a `vendor.product` token
	//    (e.g. `github.github` → `github`).
	if (connectorToken.includes('.')) {
		const trailing = connectorToken.split('.').pop();
		if (trailing && PRODUCTS_BY_TRAILING_SEGMENT[trailing]) {
			return PRODUCTS_BY_TRAILING_SEGMENT[trailing];
		}
		// 3. Leading segment of a `vendor.product` token
		//    (e.g. `microsoft.azure-devops` → `microsoft` is rare, but a
		//    valid fallback when the trailing segment isn't registered).
		const leading = connectorToken.split('.')[0];
		if (leading && PRODUCTS_BY_TRAILING_SEGMENT[leading]) {
			return PRODUCTS_BY_TRAILING_SEGMENT[leading];
		}
	} else if (PRODUCTS_BY_TRAILING_SEGMENT[connectorToken]) {
		return PRODUCTS_BY_TRAILING_SEGMENT[connectorToken];
	}

	return null;
}

/**
 * Best-effort display name resolver. Tries the supplied inputs in order until
 * a product is found. Returns `null` when nothing resolves so callers can
 * decide whether to fall back to the original text.
 */
export function getDisplayProductName(inputs: {
	thirdPartyId?: string | null;
	ari?: string | null;
	resourceOwner?: string | null;
}): string | null {
	const product =
		getProductForThirdPartyId(inputs.thirdPartyId) ??
		getProductForAri(inputs.ari) ??
		getProductByResourceOwner(inputs.resourceOwner);
	return product ? product.displayName : null;
}
